import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { getCurrentUser as queryCurrentUser } from '@/services/auth/login';
import { getMenuNav } from '@/services/menu/nav';
import type { NavMenu } from '@/services/menu/nav';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { baseURL, isDev } from '../config/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const loginPath = '/user/login';
/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  menuList?: NavMenu[];
  // 按钮级权限码集合，供 access.ts 的 hasPerm 使用
  permissions?: string[];
}> {
  const fetchUserInfo = async () => {
    try {
      const response = await queryCurrentUser({
        skipErrorHandler: true,
      });
      if (response.code === 200 && response.data) {
        return {
          name: response.data.username || response.data.name,
          avatar: response.data.avatar,
          userid: response.data.userId,
          email: response.data.email,
        };
      }
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  const isLoginPage = [loginPath, '/user/register', '/user/register-result'].includes(
    location.pathname,
  );
  console.log('location', location, location.pathname)
  if (!isLoginPage) {
    console.log('[getInitialState] 开始获取用户信息和菜单数据');
    console.log('[getInitialState] 开始调用 getMenuNav');
    const [currentUser, menuResponse] = await Promise.all([
      fetchUserInfo(),
      getMenuNav({
        skipErrorHandler: true,
      }),
    ]);
    // 获取菜单导航数据（无论用户信息是否获取成功，都尝试获取菜单）
    let menuList: NavMenu[] = [];
    let permissions: string[] = [];
    try {
      console.log('[getInitialState] getMenuNav 响应:', menuResponse);
      if (menuResponse.code === 200 && menuResponse.menuList) {
        menuList = menuResponse.menuList;
        permissions = menuResponse.permissions || [];
        console.log('[getInitialState] 菜单数据获取成功，数量:', menuList.length);
      } else {
        console.warn('[getInitialState] 菜单数据格式异常:', menuResponse);
      }
    } catch (error) {
      console.error('[getInitialState] 获取菜单导航失败:', error);
    }

    return {
      fetchUserInfo,
      currentUser,
      menuList,
      permissions,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  console.log('[getInitialState] 当前是登录/注册页面，跳过菜单获取');
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

/**
 * 检查路由路径是否在菜单列表中（递归检查 list/children）
 */
function isRouteInMenuList(routePath: string | undefined, menuList: NavMenu[]): boolean {
  if (!routePath) return false;

  // 递归检查菜单列表
  for (const menu of menuList) {
    // 检查当前菜单的 url 是否匹配路由路径
    if (menu.url === routePath) {
      return true;
    }
    // 递归检查子菜单（优先检查 list，兼容 children）
    const children = menu.list || menu.children;
    if (children && children.length > 0) {
      if (isRouteInMenuList(routePath, children)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * 检查子路由中是否有任何一个在菜单中（递归检查）
 */
function hasVisibleChildRoute(routes: any[]): boolean {
  return routes.some((route: any) => {
    // 如果当前路由不在菜单中隐藏，说明在菜单中
    if (!route.hideInMenu) {
      return true;
    }
    // 如果有子路由，递归检查（支持 routes 和 children 两种属性名）
    const childRoutes = route.routes || route.children;
    if (childRoutes && childRoutes.length > 0) {
      return hasVisibleChildRoute(childRoutes);
    }
    return false;
  });
}

/**
 * 根据菜单列表过滤路由，只显示有权限的路由
 * @param routes 全部路由配置
 * @param menuList 接口返回的有权限的菜单列表
 * @returns 过滤后的路由配置
 */
function filterRoutesByMenu(routes: any[], menuList: NavMenu[]): any[] {
  // console.log('filterRoutesByMenu', routes, menuList);
  return routes
    .map((route) => {
      // 如果是登录相关路由或404路由，保留但不在菜单中显示
      if (
        route.path === '/' ||
        route.path === '/*'
      ) {
        return {
          ...route,
          hideInMenu: true,
        };
      }

      // 如果路由本身设置了 hideInMenu: true，保留但不在菜单中显示（如详情页）
      if (route.hideInMenu === true) {
        return route;
      }

      // 检查路由是否在菜单列表中
      const isInMenu = isRouteInMenuList(route.path, menuList);
      // 如果有子路由，先递归处理子路由（支持 routes 和 children 两种属性名）
      const childRoutes = route.routes || route.children;
      // 判断原始路由使用的是哪个属性名
      const childRoutesKey = 'routes' in route ? 'routes' : 'children';

      if (childRoutes && childRoutes.length > 0) {
        const filteredChildren = filterRoutesByMenu(childRoutes, menuList);

        // 检查是否有任何子路由在菜单中（包括嵌套的子路由）
        const hasVisibleChild = hasVisibleChildRoute(filteredChildren);

        // 如果父路由本身在菜单中，或者有任何子路由在菜单中，显示父路由
        if (isInMenu || hasVisibleChild) {
          // 保留原有的属性名（routes 或 children）
          return {
            ...route,
            [childRoutesKey]: filteredChildren,
            hideInMenu: false,
          };
        } else {
          // 如果父路由和所有子路由都不在菜单中，隐藏父路由
          return {
            ...route,
            [childRoutesKey]: filteredChildren,
            hideInMenu: true,
          };
        }
      }

      // 没有子路由的情况
      if (isInMenu) {
        // 在菜单列表中，显示
        return {
          ...route,
          hideInMenu: false,
        };
      } else {
        // 不在菜单列表中，隐藏
        return {
          ...route,
          hideInMenu: true,
        };
      }
    });
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 获取菜单数据
  const menuList = initialState?.menuList || [];

  return {
    actionsRender: () => [
      <Question key="doc" />,
      // <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        ![loginPath, '/user/register', '/user/register-result'].includes(
          location.pathname,
        ) &&
        !localStorage.getItem('token')
      ) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    // links: isDev
    //   ? [
    //       <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //         <LinkOutlined />
    //         <span>OpenAPI 文档</span>
    //       </Link>,
    //     ]
    //   : [],
    menuHeaderRender: undefined,
    // 根据接口返回的菜单数据过滤路由，只显示有权限的路由
    menuDataRender: (menuData) => {
      // 如果接口返回了菜单数据，过滤路由
      if (menuList.length > 0) {
        return filterRoutesByMenu(menuData || [], menuList);
      }
      // 如果没有菜单数据，返回原始菜单数据（显示所有路由）
      return menuData || [];
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL,
  ...errorConfig,
};
